import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { products } from '@/mocks/products';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/hooks/useCart';
import ImageGallery from '@/pages/product-detail/components/ImageGallery';

function StarFill({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= Math.floor(rating);
        const partial = !filled && star === Math.ceil(rating) && rating % 1 !== 0;
        return (
          <span key={star} className={`${filled || partial ? 'text-amber-400' : 'text-stone-200'}`}>
            <i className={filled ? 'ri-star-fill' : partial ? 'ri-star-half-fill' : 'ri-star-line'} />
          </span>
        );
      })}
    </div>
  );
}

function RatingBar({ stars, count, total }: { stars: number; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-stone-500 w-8 text-right">{stars}★</span>
      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-stone-400 w-6">{count}</span>
    </div>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const product = products.find((p) => p.id === Number(id));
  const related = products.filter((p) => p.id !== Number(id) && p.category === product?.category).slice(0, 3);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#faf8f5]">
        <Navbar />
        <p className="text-2xl font-bold text-[#2c2420] mt-40">Product not found.</p>
        <Link to="/products" className="mt-4 text-[#8B6348] underline text-sm">Back to all products</Link>
        <Footer />
      </div>
    );
  }

  const ratingDist = [5, 4, 3, 2, 1].map((star) => ({
    stars: star,
    count: product.reviews.filter((r) => r.rating === star).length,
  }));

  function handleAddToCart() {
    for (let i = 0; i < qty; i++) {
      addItem({ id: product!.id, name: product!.name, price: product!.price, image: product!.image, category: product!.category });
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Navbar />

      {/* Breadcrumb */}
      <div className="w-full px-6 md:px-16 pt-32 pb-4">
        <nav className="flex items-center gap-2 text-xs text-stone-400">
          <Link to="/" className="hover:text-[#8B6348] transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/products" className="hover:text-[#8B6348] transition-colors cursor-pointer">All Products</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-[#2c2420] font-medium truncate max-w-[200px]">{product.name}</span>
        </nav>
      </div>

      {/* Main Product Section */}
      <section className="w-full px-6 md:px-16 py-8">
        <div className="flex flex-col lg:flex-row gap-12 xl:gap-20">
          {/* Image Gallery */}
          <div className="w-full lg:w-[52%] flex-shrink-0">
            <ImageGallery
              images={product.images && product.images.length > 0 ? product.images : [product.image]}
              productName={product.name}
              tag={product.tag}
            />
          </div>

          {/* Details */}
          <div className="flex-1 flex flex-col">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8B6348] mb-2 font-medium">{product.category} Sofa Bed</p>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2c2420] leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              {product.name}
            </h1>

            {/* Rating Row */}
            <div className="flex items-center gap-3 mb-6">
              <StarFill rating={product.rating} />
              <span className="text-sm font-semibold text-[#2c2420]">{product.rating.toFixed(1)}</span>
              <a href="#reviews" className="text-xs text-stone-400 hover:text-[#8B6348] transition-colors cursor-pointer">
                {product.reviewCount} reviews
              </a>
            </div>

            {/* Price */}
            <div className="mb-6">
              <span className="text-4xl font-bold text-[#8B6348]" style={{ fontFamily: "'Playfair Display', serif" }}>
                ${product.price.toLocaleString()}
              </span>
              <span className="text-xs text-stone-400 ml-2">Free shipping to Phoenix, AZ</span>
            </div>

            {/* Description */}
            <p className="text-stone-500 text-sm leading-relaxed mb-8 border-l-2 border-[#e8ddd4] pl-4">
              {product.description}
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: 'ri-time-line', text: '30-Second Conversion' },
                { icon: 'ri-shield-check-line', text: '5-Year Warranty' },
                { icon: 'ri-truck-line', text: 'Free White Glove Delivery' },
                { icon: 'ri-recycle-line', text: '30-Day Free Returns' },
              ].map((f) => (
                <div key={f.text} className="flex items-center gap-2.5 bg-white rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center text-[#8B6348]">
                    <i className={`${f.icon} text-base`} />
                  </div>
                  <span className="text-xs text-[#2c2420] font-medium">{f.text}</span>
                </div>
              ))}
            </div>

            {/* Quantity + Add to Cart */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center border border-stone-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-12 flex items-center justify-center text-stone-500 hover:bg-stone-50 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line" />
                </button>
                <span className="w-10 text-center text-sm font-semibold text-[#2c2420]">{qty}</span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="w-10 h-12 flex items-center justify-center text-stone-500 hover:bg-stone-50 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line" />
                </button>
              </div>

              <button
                onClick={handleAddToCart}
                className={`whitespace-nowrap flex-1 h-12 rounded-xl font-semibold text-sm tracking-wide transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 ${
                  added
                    ? 'bg-green-600 text-white'
                    : 'bg-[#2c2420] text-white hover:bg-[#8B6348]'
                }`}
              >
                {added ? (
                  <><i className="ri-check-line" /> Added to Cart</>
                ) : (
                  <><i className="ri-shopping-bag-line" /> Add to Cart</>
                )}
              </button>
            </div>

            <button
              onClick={() => navigate('/cart')}
              className="whitespace-nowrap w-full h-12 rounded-xl border-2 border-[#2c2420] text-[#2c2420] font-semibold text-sm tracking-wide hover:bg-[#2c2420] hover:text-white transition-all duration-300 cursor-pointer"
            >
              Buy Now
            </button>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="w-full px-6 md:px-16 py-16">
        <div className="max-w-4xl">
          <h2 className="text-2xl font-bold text-[#2c2420] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Customer Reviews
          </h2>

          <div className="flex flex-col md:flex-row gap-10 mb-10">
            {/* Summary */}
            <div className="flex flex-col items-center justify-center bg-white rounded-2xl p-8 min-w-[180px]">
              <span className="text-6xl font-bold text-[#2c2420]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {product.rating.toFixed(1)}
              </span>
              <StarFill rating={product.rating} />
              <span className="text-xs text-stone-400 mt-2">{product.reviewCount} total reviews</span>
            </div>

            {/* Rating Bars */}
            <div className="flex-1 flex flex-col justify-center gap-2.5 bg-white rounded-2xl p-6">
              {ratingDist.map((d) => (
                <RatingBar key={d.stars} stars={d.stars} count={d.count} total={product.reviews.length} />
              ))}
            </div>
          </div>

          {/* Individual Reviews */}
          <div className="flex flex-col gap-5">
            {product.reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f0e8df] text-[#8B6348] font-bold text-sm flex-shrink-0">
                    {review.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-[#2c2420]">{review.author}</span>
                      {review.verified && (
                        <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                          <i className="ri-check-line mr-0.5" />Verified Purchase
                        </span>
                      )}
                      <span className="text-xs text-stone-400 ml-auto">{review.date}</span>
                    </div>
                    <div className="mt-1 mb-2">
                      <StarFill rating={review.rating} />
                    </div>
                    <p className="text-sm font-semibold text-[#2c2420] mb-1">{review.title}</p>
                    <p className="text-sm text-stone-500 leading-relaxed">{review.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="w-full px-6 md:px-16 py-12 bg-white">
          <h2 className="text-2xl font-bold text-[#2c2420] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            You May Also Like
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                className="group bg-[#faf8f5] rounded-2xl overflow-hidden cursor-pointer block"
                data-product-shop
              >
                <div className="w-full h-[220px] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-0.5">{p.category}</p>
                  <h3 className="text-sm font-semibold text-[#2c2420]" style={{ fontFamily: "'Playfair Display', serif" }}>{p.name}</h3>
                  <p className="text-[#8B6348] font-bold text-sm mt-1">${p.price.toLocaleString()}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
